import { Capacitor } from '@capacitor/core';
import { UrlQueryKey } from 'app/core/api/api-core/api-constants';
import { useConfirmResetPassword } from 'app/core/api/auth/api-auth';
import Button from 'app/shared/components/button/Button';
import TextInput from 'app/shared/components/form-input/TextInput';
import { PW_RULES } from 'app/shared/components/form-input/utilities/form-tools';
import Icon from 'app/shared/components/icon/Icon';
import IconLib from 'app/shared/components/icon/icon-lib';
import { ErrorText, PadCollapse } from 'app/shared/components/misc';
import Heading from 'app/shared/components/typography/heading/Heading';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import Form from 'app/shared/frames/Form';
import SharedRoutePath from 'app/shared/SharedRoutePath';
import { useQueryParams } from 'app/tools/hooks/misc-hooks';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Redirect, useHistory } from 'react-router-dom';
import Body from '../components/typography/body/Body';

type FormValues = {
  newPassword1: string;
  newPassword2: string;
};

export default function ConfirmPassword(): JSX.Element {
  const { showSnack } = useShowInfo();

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { control, errors, handleSubmit, setValue, getValues } =
    useForm<FormValues>({
      mode: 'onTouched',
    });

  const {
    request$: submitChangePassword$,
    sending: submittingNewPassword,
    error: changePasswordError,
  } = useConfirmResetPassword();

  const history = useHistory();
  const params = useQueryParams();
  const uid = params.get(UrlQueryKey.UID);
  const token = params.get(UrlQueryKey.TOKEN);
  const isNative = Capacitor.isNativePlatform();

  const [submitted, setSubmitted] = useState(false);

  const onSubmit = ({ ...newPasswords }: FormValues) => {
    submitChangePassword$({
      uid: uid || '',
      token: token || '',
      ...newPasswords,
    }).subscribe({
      complete: () => {
        showSnack({
          message: `Success!${
            isNative ? ' Please log in with your new password' : ''
          }`,
          alertType: 'info',
        });
        if (isNative) history.push(SharedRoutePath.LOGIN);
        setSubmitted(true);
      },
      error: () => {
        setValue('newPassword1', '');
        setValue('newPassword2', '');
      },
    });
  };

  if (!uid || !token) {
    return <Redirect to={SharedRoutePath.WELCOME} />;
  }

  if (!isNative && submitted) {
    return (
      <>
        <Icon icon={IconLib.TICK_BOXED} soloContentStyle />
        <PadCollapse pad={3} />
        <Heading color="primary" variant="h4">
          Head back to the app
        </Heading>
        <Body>You can now login with your new password!</Body>
      </>
    );
  }

  return (
    <>
      <Icon icon={IconLib.KEY_BOXED} soloContentStyle />
      <Heading color="primary" variant="h4">
        Create new password
      </Heading>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <TextInput
          placeholder="New password"
          autoComplete="new-password"
          type="password"
          icon={IconLib.LOCK}
          errorText={errors.newPassword1?.message}
          controller={{
            control,
            name: 'newPassword1',
            rules: PW_RULES,
          }}
        />
        <TextInput
          placeholder="Confirm new password"
          autoComplete="new-password"
          type="password"
          icon={IconLib.LOCK}
          errorText={errors.newPassword2?.message}
          controller={{
            control,
            name: 'newPassword2',
            rules: {
              required: 'Required',
              validate: {
                matchesPassword: (text: string) =>
                  text === getValues('newPassword1') ||
                  'Both new password entries must match',
              },
            },
          }}
        />
        <ErrorText
          message={
            changePasswordError?.token
              ? 'Invalid token. Please request a new password reset'
              : changePasswordError
          }
        />
        <Button type="submit" pending={submittingNewPassword}>
          Reset password
        </Button>
      </Form>
    </>
  );
}
